import { memo, useState } from "react";
import { useNavigate } from "react-router";
import { Image, Typography } from "antd";
import { EnvironmentOutlined, EyeOutlined } from "@ant-design/icons";

import LazyJoinTheMomentModal from "@/components/joinMoment/LazyJoinTheMomentModal";
import T from "@/components/common/T";
import Cover from "./Cover";
import { NAVY, BRAND, BRAND_GRADIENT } from "./constants";
import { useGlobalStore } from "@/context/useGlobalStore";
import useSessionStore from "@/hooks/useSessionStore";
import { getEventDetailsRoute } from "@/utils/eventPage/helpers";

const { Text, Paragraph } = Typography;

// City / global walk card: cover image, location, description, and a Join CTA.
const WalkCard = ({ walk }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const navigateTo = useNavigate();
    const walkerIPDetails = useGlobalStore((s) => s.walkerIPDetails);
    const groupMemberIPDetails = useGlobalStore((s) => s.groupMemberIPDetails);
    const setState = useGlobalStore((s) => s.setState);
    const getStateSession = useSessionStore((s) => s.getState);

    const isAlreadyJoined = walk?.joinType === "group-walks"
        ? (
            groupMemberIPDetails?.joined_group_walks?.map(({ group_walk_id }) => Number(group_walk_id))?.includes(Number(walk?.id)) ||
            getStateSession("myGroupWalks")?.includes(walk?.id) ||
            false
        )
        : (
            walkerIPDetails?.joined_walks?.map(({ walk_id }) => Number(walk_id))?.includes(Number(walk?.id)) ||
            getStateSession("myWalks")?.includes(walk?.id) ||
            false
        );

    const openWalkPage = () => {
        // Close the outer "Join a Walk" drawer so the destination walk/
        // group-walk page is visible, same signal CreateWalk's pin flow uses.
        setState("joinWalkDrawerClose", Date.now());
        navigateTo(getEventDetailsRoute(walk.joinType, walk.id));
    };

    return (
        <div className="jw-card jw-walkcard">
            <Cover
                src={walk.image}
                alt={walk.name}
                title={walk.name}
                height={140}
                onImageClick={()=>setPreviewOpen(true)}
                action={walk.image && (
                    <button
                        type="button"
                        className="jw-view-btn"
                        aria-label="View image"
                        onClick={(e) => { e.stopPropagation(); openWalkPage(); }}
                        style={{
                            width: 26, height: 26, display: "grid", placeItems: "center",
                            borderRadius: "50%", border: "none", cursor: "pointer",
                            background: "rgba(0,0,0,.45)", color: "#fff",
                        }}
                    >
                        <EyeOutlined />
                    </button>
                )}
            />
            {walk.image && (
                <Image
                    src={walk.image}
                    alt={walk.name}
                    style={{ display: "none" }}
                    preview={{ visible: previewOpen, onVisibleChange: setPreviewOpen }}
                />
            )}
            <div className="jw-body">
                {walk.location && (
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                        <EnvironmentOutlined style={{ color: BRAND, marginInlineEnd: 4 }} />
                        {walk.location}
                    </Text>
                )}
                {walk.description && (
                    <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 14, fontSize: 13 }}>
                        {walk.description}
                    </Paragraph>
                )}
                <div className="jw-join" onClick={(e) => e.stopPropagation()}>
                    <LazyJoinTheMomentModal
                        details={walk}
                        type={walk.joinType}
                        isAlreadyJoined={isAlreadyJoined}
                        joinBtnProps={{
                            children: isAlreadyJoined ? <T k="Joined" /> : <T k="JOIN THIS WALK" />,
                            block: true,
                            disabled: isAlreadyJoined,
                            style: {
                                height: 42, borderRadius: 999, fontWeight: 700, letterSpacing: 0.5,
                                border: "none", background: BRAND_GRADIENT, color: NAVY,
                                opacity: isAlreadyJoined ? 0.5 : 1,
                                filter: isAlreadyJoined ? "grayscale(60%)" : "none",
                                cursor: isAlreadyJoined ? "not-allowed" : "pointer",
                            },
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(WalkCard);
